import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { TransactionEvents } from '@app/domain/events';
import { TransferMatchingDomainService } from '@app/domain/services';
import {
  ConvertExpenseIncomeToTransferUseCase,
  EvaluateTransferMatchUseCase,
  FindTransferCandidatesUseCase,
  GetCurrencyRateUseCase,
  InspectSplitTransferUseCase,
  MatchImportedLeftoverTransfersUseCase,
  SplitAccountTransferUseCase,
} from '@app/domain/use-cases';
import type {
  IAccountTransferRepository,
  IExpenseRepository,
  IExternalTransactionRepository,
  IIncomeRepository,
  ITransactionManager,
} from '@app/domain/ports';
import {
  ACCOUNT_TRANSFER_REPOSITORY,
  EXPENSE_REPOSITORY,
  EXTERNAL_TRANSACTION_REPOSITORY,
  INCOME_REPOSITORY,
  TRANSACTION_MANAGER,
} from '../../tokens/infrastructure.tokens';

export function provideTransferMatchingDomain(): Provider[] {
  return [
    {
      provide: TransferMatchingDomainService,
      useFactory: () => new TransferMatchingDomainService(),
      deps: [],
    },
    {
      provide: FindTransferCandidatesUseCase,
      useFactory: (
        expenseRepository: IExpenseRepository,
        incomeRepository: IIncomeRepository,
        accountTransferRepository: IAccountTransferRepository,
        matching: TransferMatchingDomainService,
      ) => new FindTransferCandidatesUseCase(expenseRepository, incomeRepository, accountTransferRepository, matching),
      deps: [EXPENSE_REPOSITORY, INCOME_REPOSITORY, ACCOUNT_TRANSFER_REPOSITORY, TransferMatchingDomainService],
    },
    {
      provide: EvaluateTransferMatchUseCase,
      useFactory: (
        findTransferCandidates: FindTransferCandidatesUseCase,
        expenseRepository: IExpenseRepository,
        incomeRepository: IIncomeRepository,
        matching: TransferMatchingDomainService,
      ) => new EvaluateTransferMatchUseCase(findTransferCandidates, expenseRepository, incomeRepository, matching),
      deps: [FindTransferCandidatesUseCase, EXPENSE_REPOSITORY, INCOME_REPOSITORY, TransferMatchingDomainService],
    },
    {
      provide: ConvertExpenseIncomeToTransferUseCase,
      useFactory: (
        expenseRepository: IExpenseRepository,
        incomeRepository: IIncomeRepository,
        accountTransferRepository: IAccountTransferRepository,
        externalTransactionRepository: IExternalTransactionRepository,
        transactionManager: ITransactionManager,
        matching: TransferMatchingDomainService,
      ) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new ConvertExpenseIncomeToTransferUseCase(
          expenseRepository,
          incomeRepository,
          accountTransferRepository,
          externalTransactionRepository,
          transactionManager,
          matching,
          dispatch,
        );
      },
      deps: [
        EXPENSE_REPOSITORY,
        INCOME_REPOSITORY,
        ACCOUNT_TRANSFER_REPOSITORY,
        EXTERNAL_TRANSACTION_REPOSITORY,
        TRANSACTION_MANAGER,
        TransferMatchingDomainService,
      ],
    },
    {
      provide: InspectSplitTransferUseCase,
      useFactory: (
        accountTransferRepository: IAccountTransferRepository,
        expenseRepository: IExpenseRepository,
        incomeRepository: IIncomeRepository,
      ) => new InspectSplitTransferUseCase(accountTransferRepository, expenseRepository, incomeRepository),
      deps: [ACCOUNT_TRANSFER_REPOSITORY, EXPENSE_REPOSITORY, INCOME_REPOSITORY],
    },
    {
      provide: SplitAccountTransferUseCase,
      useFactory: (
        accountTransferRepository: IAccountTransferRepository,
        expenseRepository: IExpenseRepository,
        incomeRepository: IIncomeRepository,
        getCurrencyRateUseCase: GetCurrencyRateUseCase,
        transactionManager: ITransactionManager,
      ) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new SplitAccountTransferUseCase(
          accountTransferRepository,
          expenseRepository,
          incomeRepository,
          getCurrencyRateUseCase,
          transactionManager,
          dispatch,
        );
      },
      deps: [
        ACCOUNT_TRANSFER_REPOSITORY,
        EXPENSE_REPOSITORY,
        INCOME_REPOSITORY,
        GetCurrencyRateUseCase,
        TRANSACTION_MANAGER,
      ],
    },
    {
      provide: MatchImportedLeftoverTransfersUseCase,
      useFactory: (
        findTransferCandidates: FindTransferCandidatesUseCase,
        convert: ConvertExpenseIncomeToTransferUseCase,
        matching: TransferMatchingDomainService,
      ) => new MatchImportedLeftoverTransfersUseCase(findTransferCandidates, convert, matching),
      deps: [FindTransferCandidatesUseCase, ConvertExpenseIncomeToTransferUseCase, TransferMatchingDomainService],
    },
  ];
}
